// @flow
import React from 'react';
import { isEmpty } from 'lodash';

import { ID_SIDEPANEL_HEADER__SHOPPING__WIBILONG_NARROW } from 'Components/utils/identifiers/constants';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { t } from 'Src/i18n';

import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes.jsx';
import { Notification } from 'Components/frameworks/notification/Notification.jsx';
import { PostAnswer, type PostAnswerProps } from 'Components/frameworks/post-answer/PostAnswer.jsx';
import {
  PostQuestion,
  type PostQuestionProps,
} from 'Components/frameworks/post-question/PostQuestion.jsx';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable.jsx';
import { SidePanel } from 'Components/frameworks/side-panel/SidePanel.jsx';

import { WibilongCatchBlock } from 'Components/widgets/wibilong/catchblock/WibilongCatchBlock.jsx';
import { type WibilongPopinButtonProps } from 'Components/widgets/wibilong/popin-button/WibilongPopinButton.jsx';

type Question = {
  ...$Exact<PostQuestionProps>,
  answers: Array<PostAnswerProps>,
  trackingData?: TrackingData,
};

type Link = {
  label?: string,
  target?: string,
  trackingData?: TrackingData,
  url?: string,
};

type Props = {
  button: WibilongPopinButtonProps,
  hasLinkAllQuestions?: boolean,
  linkAllQuestions?: Link,
  noAnswerstext?: string,
  questions: Array<Question>,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
  seeMoreLabel?: string,
  text?: string,
};

export function WibilongNarrow(props: Props) {
  const {
    button,
    hasLinkAllQuestions,
    linkAllQuestions = {},
    noAnswerstext,
    questions = [],
    scrollBackwardLabel,
    scrollForwardLabel,
    seeMoreLabel,
    text,
  } = props;

  if (isEmpty(questions)) return null;

  const boxStyles = { minHeight: 230, width: '80vw' };

  return (
    <ActiveIndexes hasNoIndexEnabled>
      {({ update, activeIndexes, clear }) => {
        const activeQuestion = questions[activeIndexes[0]] || {};

        const answers = activeQuestion.answers ? activeQuestion.answers : [];

        const ariaLabel = activeQuestion.title;
        const ariaLabelledby =
          activeQuestion.id &&
          `${ID_SIDEPANEL_HEADER__SHOPPING__WIBILONG_NARROW}-${activeQuestion.id}`;

        return (
          <div className="mt-6">
            <Scrollable
              backwardLabel={scrollBackwardLabel}
              forwardLabel={scrollForwardLabel}
              navPosition="center"
            >
              <div className="flex px-4">
                {questions.map((q, i) => (
                  <div className="flex-none p-2" key={q.id} style={boxStyles}>
                    <TrackClick
                      as="div"
                      className="bg-white cursor-pointer h-full p-5 rounded shadow-md"
                      data-testid="Wibilong-question"
                      onClick={() => update(i)}
                      role="button"
                      tabIndex={0}
                      trackingData={q.trackingData}
                    >
                      <PostQuestion
                        {...q}
                        className="h-full"
                        seeMoreLabel={!isEmpty(q.answers) ? seeMoreLabel : ''}
                      />
                    </TrackClick>
                  </div>
                ))}
                <div className="pr-4 pointer-events-none" />
              </div>
            </Scrollable>
            <SidePanel
              ariaLabel={ariaLabel}
              ariaLabelledby={ariaLabelledby}
              className="px-4 py-5"
              closeLabel={t('common.close')}
              isExpanded={!isEmpty(activeIndexes)}
              onClose={clear}
            >
              {!isEmpty(activeIndexes) && (
                <div className="fadeIn">
                  <PostQuestion
                    {...questions[activeIndexes[0]]}
                    className="bg-white cursor-pointer flex-none m-2 p-5 rounded shadow-md"
                    textLimit={-1}
                    titleId={ariaLabelledby}
                  />
                  <div className="border-t-1 mt-8">
                    {!isEmpty(answers) ? (
                      answers.map(answer => (
                        <PostAnswer {...answer} className="py-8 border-b-1" key={answer.id} />
                      ))
                    ) : (
                      <Notification
                        className="mt-8"
                        closeLabel={ariaLabel ? [t('common.close'), ariaLabel].join(' - ') : ''}
                        hasBorder
                        hasClose
                        onClose={clear}
                        text={noAnswerstext}
                      />
                    )}
                    {hasLinkAllQuestions && (
                      <WibilongCatchBlock
                        className="mt-8"
                        button={button}
                        link={linkAllQuestions}
                        text={text}
                      />
                    )}
                  </div>
                </div>
              )}
            </SidePanel>
          </div>
        );
      }}
    </ActiveIndexes>
  );
}
